const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface VerificationSubmitData {
  // Contact Information
  contactPersonName: string;
  contactPersonRelation: string;
  numberOfFamilyMembers?: number;
  contactPhoneNo?: string;

  // ID Proof
  idProofType: 'aadhaar' | 'pan' | 'voter_id' | 'passport' | 'driving_license' | 'other';
  idProofNumber?: string;

  // Address Details
  periodOfStay?: string;
  differentAddress?: string;
  residentialStatus: 'owned' | 'rented' | 'company_provided' | 'pg' | 'other';
  landmark?: string;
  remarks?: string;

  // Geolocation
  latitude?: number;
  longitude?: number;

  // Legacy fields
  addressConfirmed?: boolean;
  correctedAddress?: string;
  candidateComments?: string;
}

class ApiService {
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getAuthToken();

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      const data = await response.json();

      if (!response.ok) {
        const isAuthEndpoint = endpoint.startsWith('/auth/login');

        if (response.status === 401 && !isAuthEndpoint) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }

          throw new Error(data.message || 'Authentication required');
        }

        // Vendor hasn't signed the mandatory agreement — route to the gate
        // (which lives at /dashboard behind VendorGuard).
        if (response.status === 403 && data?.code === 'AGREEMENT_NOT_SIGNED') {
          if (window.location.pathname !== '/dashboard') {
            window.location.href = '/dashboard';
          }
          throw new Error(data.message || 'Agreement not signed');
        }

        // Keep the body: some endpoints return a list of problems (e.g. the
        // form builder's `errors`) that the caller needs to show.
        const httpError = new Error(data.message || `HTTP error! status: ${response.status}`) as Error & { data?: any; status?: number };
        httpError.data = data;
        httpError.status = response.status;
        throw httpError;
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Cases API methods
  async getCases(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<{
    cases: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);

    const endpoint = queryParams.toString() ? `/cases?${queryParams}` : '/cases';
    return this.get(endpoint);
  }

  async getCaseStats(): Promise<ApiResponse<{
    stats: {
      totalCases: number;
      pendingCases: number;
      completedCases: number;
      insufficiencyCases: number;
      digiLockerInitiated: number;
      digiLockerCompleted: number;
    };
  }>> {
    return this.get('/cases/stats/overview');
  }

  async getCaseById(caseId: string): Promise<ApiResponse<{
    case: any;
  }>> {
    return this.get(`/cases/${caseId}`);
  }

  async createCase(caseData: any): Promise<ApiResponse<{
    case: any;
  }>> {
    return this.post('/cases', caseData);
  }

  async updateCase(caseId: string, caseData: any): Promise<ApiResponse<{
    case: any;
  }>> {
    return this.put(`/cases/${caseId}`, caseData);
  }

  async deleteCase(caseId: string): Promise<ApiResponse<{
    case: any;
  }>> {
    return this.delete(`/cases/${caseId}`);
  }

  async bulkCreateCases(cases: any[]): Promise<ApiResponse<{
    created: number;
    failed: number;
    errors?: any[];
  }>> {
    return this.post('/cases/bulk', { cases });
  }

  // DigiLocker specific methods
  async initiateDigiLocker(caseData: any): Promise<ApiResponse<{
    sessionId: string;
    authUrl: string;
    verificationLink: string;
    notificationStatus: {
      emailSent: boolean;
      smsSent: boolean;
    };
  }>> {
    return this.post('/digilocker/initiate', {
      caseId: caseData.id,
      caseData: {
        name: caseData.name,
        phone: caseData.phone,
        email: caseData.email || '',
        companyName: caseData.companyName,
        code: caseData.code
      },
      redirectUrl: `${window.location.origin}/digilocker/callback`
    });
  }

  async getDigiLockerSession(sessionId: string): Promise<ApiResponse<any>> {
    return this.get(`/digilocker/session/${sessionId}`);
  }

  async sendDigiLockerAuthEmail(caseData: any): Promise<ApiResponse<{
    sessionId: string;
    authUrl: string;
    emailSent: boolean;
  }>> {
    return this.post('/digilocker/send-auth-email', {
      userEmail: caseData.email,
      // userEmail: 'maheshwariharsh38@gmail.com',
      caseData: {
        name: caseData.name,
        initiatorName: caseData.initiatorName,
        phone: caseData.phone,
        email: caseData.email || '',
        companyName: caseData.companyName,
        code: caseData.code,
        caseId: caseData.id,
        address: caseData.address,
        city: caseData.city,
        state: caseData.state,
        pin: caseData.pin
      }
    });
  }

  // Authentication methods
  async login(email: string, password: string, ipAddress?: string): Promise<ApiResponse<{
    email: string;
    otpRequired: true;
  }>> {
    const payload: any = { email, password };
    if (ipAddress) payload.ipAddress = ipAddress;
    return this.post('/auth/login', payload);
  }

  async verifyLoginOtp(email: string, otp: string): Promise<ApiResponse<
    | { token: string; user: any }
    | { passwordResetRequired: true; resetToken: string }
  >> {
    return this.post('/auth/login/verify-otp', { email, otp });
  }

  async resendLoginOtp(email: string): Promise<ApiResponse<{ retryInSec?: number }>> {
    return this.post('/auth/login/resend-otp', { email });
  }

  // ── Forgot password ────────────────────────────────────────────────────
  async forgotPassword(email: string): Promise<ApiResponse<{}>> {
    return this.post('/auth/forgot-password', { email });
  }

  async verifyForgotPasswordOtp(email: string, otp: string): Promise<ApiResponse<{ resetToken: string }>> {
    return this.post('/auth/forgot-password/verify-otp', { email, otp });
  }

  async resendForgotPasswordOtp(email: string): Promise<ApiResponse<{ retryInSec?: number }>> {
    return this.post('/auth/forgot-password/resend-otp', { email });
  }

  async forceResetPassword(newPassword: string, resetToken: string): Promise<ApiResponse<{ token: string; user: any }>> {
    return this.request('/auth/force-reset-password', {
      method: 'POST',
      headers: { Authorization: `Bearer ${resetToken}` },
      body: JSON.stringify({ newPassword })
    });
  }

  async logout(): Promise<ApiResponse<any>> {
    return this.post('/auth/logout');
  }

  async getDashboardActivity(limit = 10): Promise<ApiResponse<{
    activity: Array<{
      type: 'case' | 'address' | 'document' | 'report';
      id: string;
      name: string;
      status: string;
      time: string;
    }>;
  }>> {
    return this.get(`/dashboard/activity?limit=${limit}`);
  }

  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    entityType?: string;
    severity?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{
    logs: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalLogs: number;
      limit: number;
    };
  }>> {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
    });
    const query = q.toString();
    return this.get(`/admin/audit-logs${query ? `?${query}` : ''}`);
  }

  async getAuditLogStats(): Promise<ApiResponse<any>> {
    return this.get('/admin/audit-logs/stats');
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: any }>> {
    return this.get('/auth/me');
  }

  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    return this.post('/auth/refresh');
  }

  async resetPassword(currentPassword: string, newPassword: string): Promise<ApiResponse<any>> {
    return this.post('/auth/reset-password', { currentPassword, newPassword });
  }

  async updateProfile(name: string, designation: string, phone?: string): Promise<ApiResponse<{ user: any }>> {
    return this.put('/auth/profile', { name, designation, phone });
  }

  // Customers API methods
  async getCustomers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    lastUpdatedFrom?: string;
    lastUpdatedTo?: string;
    lastReportSentFrom?: string;
    lastReportSentTo?: string;
  }): Promise<ApiResponse<{
    customers: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params?.lastUpdatedFrom) queryParams.append('lastUpdatedFrom', params.lastUpdatedFrom);
    if (params?.lastUpdatedTo) queryParams.append('lastUpdatedTo', params.lastUpdatedTo);
    if (params?.lastReportSentFrom) queryParams.append('lastReportSentFrom', params.lastReportSentFrom);
    if (params?.lastReportSentTo) queryParams.append('lastReportSentTo', params.lastReportSentTo);

    const endpoint = queryParams.toString() ? `/customers?${queryParams}` : '/customers';
    return this.get(endpoint);
  }

  async getCustomerById(customerId: string): Promise<ApiResponse<{
    customer: any;
  }>> {
    return this.get(`/customers/${customerId}`);
  }

  async createCustomer(customerData: any): Promise<ApiResponse<{
    customer: any;
  }>> {
    return this.post('/customers', customerData);
  }

  async updateCustomer(customerId: string, customerData: any): Promise<ApiResponse<{
    customer: any;
  }>> {
    return this.put(`/customers/${customerId}`, customerData);
  }

  async updateCustomerBgvFormConfig(customerId: string, bgvFormConfig: any): Promise<ApiResponse<{
    customer: any;
  }>> {
    return this.put(`/customers/${customerId}/bgv-form-config`, { bgvFormConfig });
  }

  // ===== BGV config groups =====
  // A group is a named BGV config preset belonging to one company. Candidates
  // assigned to a group resolve their form config from it until they submit.

  async getBgvGroups(customerId: string): Promise<ApiResponse<any>> {
    return this.get(`/bgv-groups?customerId=${encodeURIComponent(customerId)}`);
  }

  async getBgvGroupById(groupId: string): Promise<ApiResponse<any>> {
    return this.get(`/bgv-groups/${groupId}`);
  }

  async createBgvGroup(payload: {
    customerId: string;
    name: string;
    description?: string;
    config?: any;
    /** 'inherit': the group uses the company form until its own is edited. */
    formTemplateMode?: 'inherit';
  }): Promise<ApiResponse<any>> {
    return this.post('/bgv-groups', payload);
  }

  async updateBgvGroup(groupId: string, payload: {
    name?: string;
    description?: string;
    config?: any;
  }): Promise<ApiResponse<any>> {
    return this.put(`/bgv-groups/${groupId}`, payload);
  }

  /**
   * Preview what saving `config` would do to this group's pending candidates.
   * Read-only despite being a POST -- the proposed config travels in the body.
   */
  async getBgvGroupImpact(groupId: string, config: any): Promise<ApiResponse<any>> {
    return this.post(`/bgv-groups/${groupId}/impact`, { config });
  }

  async deleteBgvGroup(groupId: string): Promise<ApiResponse<any>> {
    return this.delete(`/bgv-groups/${groupId}`);
  }

  // ===== BGV tracker =====
  // See Zella-Screenings-backend/routes/tracker.js. Company logins get only
  // their own cases (read-only) from the same endpoints.

  private trackerQuery(params: Record<string, any> = {}): string {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
      q.set(k, Array.isArray(v) ? v.join(',') : String(v));
    }
    const s = q.toString();
    return s ? `?${s}` : '';
  }

  async getTrackerMeta(): Promise<ApiResponse<any>> {
    return this.get('/tracker/meta');
  }

  async getTrackerCases(params: Record<string, any>): Promise<ApiResponse<any>> {
    return this.get(`/tracker/cases${this.trackerQuery(params)}`);
  }

  async getTrackerCase(id: string): Promise<ApiResponse<any>> {
    return this.get(`/tracker/cases/${id}`);
  }

  async createTrackerCase(data: any): Promise<ApiResponse<any>> {
    return this.post('/tracker/cases', data);
  }

  async updateTrackerCase(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/tracker/cases/${id}`, data);
  }

  async deleteTrackerCase(id: string): Promise<ApiResponse<any>> {
    return this.delete(`/tracker/cases/${id}`);
  }

  async restoreTrackerCase(id: string): Promise<ApiResponse<any>> {
    return this.post(`/tracker/cases/${id}/restore`);
  }

  /** `all` + `filters` (super-admins, delete only): every case matching the filters, refused if more than `expectedCount` match. */
  async bulkTrackerCases(payload: { ids?: string[]; action: 'update' | 'delete'; values?: any; all?: boolean; filters?: Record<string, any>; expectedCount?: number }): Promise<ApiResponse<any>> {
    return this.post('/tracker/cases/bulk', payload);
  }

  /** Undo a bulk removal. */
  async restoreTrackerBatch(deleteBatch: string): Promise<ApiResponse<any>> {
    return this.post('/tracker/cases/restore-batch', { deleteBatch });
  }

  async getTrackerAnalytics(params: Record<string, any>): Promise<ApiResponse<any>> {
    return this.get(`/tracker/analytics${this.trackerQuery(params)}`);
  }

  /** Add a company from the tracker. Creates no logins (those are created when the company is emailed from Datahub). Emails are optional. */
  async createTrackerCompany(data: { companyName: string; emails?: string[] }): Promise<ApiResponse<any>> {
    return this.post('/tracker/companies', data);
  }

  /** Create one company per name (names already on the platform are returned, not created again). */
  async createTrackerCompanies(names: string[]): Promise<ApiResponse<any>> {
    return this.post('/tracker/companies/bulk', { names });
  }

  /** A company's own columns (remembered from its import), or null. */
  async getTrackerLayout(customerId: string): Promise<ApiResponse<{ companyName: string; layout: any[] | null }>> {
    return this.get(`/tracker/layout/${customerId}`);
  }

  /** The shared tracker tabs (everyone sees the same set). */
  async getTrackerTabs(): Promise<ApiResponse<any>> {
    return this.get('/tracker/tabs');
  }

  /** Super-admins only. */
  async saveTrackerTabs(data: { tabs: any[] }): Promise<ApiResponse<any>> {
    return this.put('/tracker/tabs', data);
  }

  async saveTrackerSettings(customerId: string, data: {
    trackerTatDays?: number;
    trackerVisibleFields?: string[];
    trackerCustomFields?: { label: string; type: string }[];
    resetLayout?: boolean;
    trackerEnabled?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.put(`/tracker/settings/${customerId}`, data);
  }

  /** Show or hide the BGV tracker for every company's logins. */
  async setTrackerAccessForAll(enabled: boolean): Promise<ApiResponse<{ enabled: boolean; count: number }>> {
    return this.put('/tracker/settings/tracker-access', { enabled });
  }

  /** Whether this login may open the BGV tracker (company logins can have it turned off). */
  async getTrackerAccess(): Promise<ApiResponse<{ enabled: boolean }>> {
    return this.get('/tracker/access');
  }

  /** Download the Excel export for the given filters. */
  async downloadTrackerExport(params: Record<string, any>): Promise<void> {
    const token = this.getAuthToken();
    const res = await fetch(`${API_BASE_URL}/tracker/export${this.trackerQuery(params)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.message || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const name = disposition.match(/filename="([^"]+)"/)?.[1] || 'BGV_Tracker.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async trackerImportAnalyze(file: File): Promise<ApiResponse<any>> {
    const token = this.getAuthToken();
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE_URL}/tracker/import/analyze`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(body?.message || `Upload failed (${res.status})`);
    return body;
  }

  async trackerImportPreview(payload: { token: string; sheets: any[] }): Promise<ApiResponse<any>> {
    return this.post('/tracker/import/preview', payload);
  }

  async trackerImportApply(payload: { token: string; sheets: any[] }): Promise<ApiResponse<any>> {
    return this.post('/tracker/import/apply', payload);
  }

  // ===== BGV form builder =====
  // A company or group form is a template; every save writes a new immutable
  // version. See Zella-Screenings-backend/routes/bgvFormTemplates.js.

  async getBgvFormTemplate(ownerType: 'company' | 'group', ownerId: string): Promise<ApiResponse<any>> {
    return this.get(`/bgv-form-templates/${ownerType}/${ownerId}`);
  }

  async getBgvFormTemplateVersion(versionId: string): Promise<ApiResponse<any>> {
    return this.get(`/bgv-form-templates/versions/${versionId}`);
  }

  async getBgvFormTemplateImpact(ownerType: 'company' | 'group', ownerId: string, definition: any): Promise<ApiResponse<any>> {
    return this.post(`/bgv-form-templates/${ownerType}/${ownerId}/impact`, { definition });
  }

  async saveBgvFormTemplate(ownerType: 'company' | 'group', ownerId: string, payload: {
    definition: any;
    note?: string;
    applyToNotStarted?: boolean;
    reissueStarted?: boolean;
    batchId?: string;
    restoredFrom?: string;
  }): Promise<ApiResponse<any>> {
    return this.post(`/bgv-form-templates/${ownerType}/${ownerId}`, payload);
  }

  async setBgvGroupFormMode(groupId: string, mode: 'inherit' | 'custom'): Promise<ApiResponse<any>> {
    return this.put(`/bgv-form-templates/group/${groupId}/mode`, { mode });
  }

  /**
   * Archive progress, expire the current link and email a new one on the
   * latest form. Unlike send-link, this always issues a new token.
   * `batchId` must be generated once per click so a duplicated request is a no-op.
   */
  async reissueDocumentCollectionLinks(payload: {
    ids?: string[];
    customerId?: string;
    bgvGroupId?: string;
    batchId: string;
  }): Promise<ApiResponse<any>> {
    return this.post('/document-collections/reissue-links', payload);
  }

  async deleteCustomer(customerId: string): Promise<ApiResponse<{
    customer: any;
  }>> {
    return this.delete(`/customers/${customerId}`);
  }

  async bulkSetCustomerPasswordReminders(
    customerIds: string[],
    enabled: boolean
  ): Promise<ApiResponse<{ matched: number; modified: number }>> {
    return this.post('/customers/bulk-password-reminders', { customerIds, enabled });
  }

  async setCustomerPasswordReminders(
    customerId: string,
    enabled: boolean
  ): Promise<ApiResponse<{ customer: any }>> {
    return this.patch(`/customers/${customerId}/password-reminders`, { enabled });
  }

  // Vendors API methods (super-admin only)
  async getVendors(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    location?: string;
  }): Promise<ApiResponse<{
    vendors: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params?.location) queryParams.append('location', params.location);

    const endpoint = queryParams.toString() ? `/vendors?${queryParams}` : '/vendors';
    return this.get(endpoint);
  }

  async getVendorById(vendorId: string): Promise<ApiResponse<{ vendor: any }>> {
    return this.get(`/vendors/${vendorId}`);
  }

  async createVendor(vendorData: any): Promise<ApiResponse<{ vendor: any; userProvisioned: boolean }>> {
    return this.post('/vendors', vendorData);
  }

  async updateVendor(vendorId: string, vendorData: any): Promise<ApiResponse<{ vendor: any }>> {
    return this.put(`/vendors/${vendorId}`, vendorData);
  }

  async deleteVendor(vendorId: string): Promise<ApiResponse<{ vendor: any }>> {
    return this.delete(`/vendors/${vendorId}`);
  }

  // Permanently deletes the vendor + its logins (super-admin only, irreversible).
  // Cases previously assigned to this vendor are kept, just unlinked.
  async permanentlyDeleteVendor(vendorId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/vendors/${vendorId}/permanent`);
  }

  // ── Vendor portal (vendor / vendor-member) ──────────────────────────────
  async getMyVendor(): Promise<ApiResponse<{ vendor: any; role: string }>> {
    return this.get('/vendors/me');
  }

  // ── Vendor agreement (mandatory e-signature) ────────────────────────────
  async getVendorAgreement(): Promise<ApiResponse<{
    version: string;
    title: string;
    sections: { heading: string | null; body: string }[];
    vendorName: string;
    vendorAddress: string;
    status: { signed: boolean; signedName: string; signedAt: string | null; agreementVersion: string };
  }>> {
    return this.get('/vendors/agreement');
  }

  async signVendorAgreement(signedName: string): Promise<ApiResponse<{
    status: { signed: boolean; signedName: string; signedAt: string | null; agreementVersion: string };
  }>> {
    return this.post('/vendors/agreement/sign', { signedName });
  }

  async getVendorMembers(): Promise<ApiResponse<{ members: any[] }>> {
    return this.get('/vendors/me/members');
  }

  async addVendorMember(payload: { name: string; email: string }): Promise<ApiResponse<{ member: any }>> {
    return this.post('/vendors/me/members', payload);
  }

  async deleteVendorMember(userId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/vendors/me/members/${userId}`);
  }

  async assignCaseMember(verificationId: string, memberId: string): Promise<ApiResponse<{ assignedMember: any }>> {
    return this.put(`/address-verifications/${verificationId}/assign-member`, { memberId });
  }

  async saveVendorWork(verificationId: string, payload: any): Promise<ApiResponse<{ vendorWork: any }>> {
    return this.put(`/address-verifications/${verificationId}/vendor-work`, payload);
  }

  async uploadVendorPhoto(
    verificationId: string,
    file: File,
    coords: { latitude?: number; longitude?: number }
  ): Promise<ApiResponse<{ photo: any }>> {
    const token = this.getAuthToken();
    const formData = new FormData();
    formData.append('photo', file);
    if (coords.latitude !== undefined) formData.append('latitude', String(coords.latitude));
    if (coords.longitude !== undefined) formData.append('longitude', String(coords.longitude));

    const response = await fetch(`${API_BASE_URL}/address-verifications/${verificationId}/vendor-photo`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') window.location.href = '/login';
        throw new Error('Authentication required');
      }
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async uploadVendorDocument(verificationId: string, file: File): Promise<ApiResponse<{ document: any }>> {
    const token = this.getAuthToken();
    const formData = new FormData();
    formData.append('document', file);

    const response = await fetch(`${API_BASE_URL}/address-verifications/${verificationId}/vendor-document`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') window.location.href = '/login';
        throw new Error('Authentication required');
      }
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async deleteVendorPhoto(verificationId: string, photoId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/address-verifications/${verificationId}/vendor-photo/${photoId}`);
  }

  async deleteVendorDocument(verificationId: string, docId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/address-verifications/${verificationId}/vendor-document/${docId}`);
  }

  // Customer Documents API
  async getCustomerDocuments(customerId: string, params?: { dateFrom?: string; dateTo?: string }): Promise<ApiResponse<{
    documents: any[];
    documentsRequired: string[];
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    const endpoint = queryParams.toString()
      ? `/customers/${customerId}/documents?${queryParams}`
      : `/customers/${customerId}/documents`;
    return this.get(endpoint);
  }

  async uploadCustomerDocument(customerId: string, formData: FormData): Promise<ApiResponse<{
    document: any;
  }>> {
    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/customers/${customerId}/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    return response.json();
  }

  async deleteCustomerDocument(customerId: string, documentId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/customers/${customerId}/documents/${documentId}`);
  }

  // Document Requirements API
  async addDocumentRequirement(customerId: string, documentName: string): Promise<ApiResponse<{
    documentsRequired: string[];
  }>> {
    return this.post(`/customers/${customerId}/document-requirements`, { documentName });
  }

  async renameDocumentRequirement(customerId: string, index: number, newName: string): Promise<ApiResponse<{
    documentsRequired: string[];
  }>> {
    return this.put(`/customers/${customerId}/document-requirements/${index}`, { newName });
  }

  async deleteDocumentRequirement(customerId: string, index: number): Promise<ApiResponse<{
    documentsRequired: string[];
  }>> {
    return this.delete(`/customers/${customerId}/document-requirements/${index}`);
  }

  async downloadAllDocuments(customerId: string): Promise<void> {
    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/customers/${customerId}/documents/download-all`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download documents');
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'documents.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Download the blob
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async sendCustomerLoginEmails(customerId: string, emails: string[]): Promise<ApiResponse<{
    results: any[];
    successCount: number;
    failureCount: number;
  }>> {
    return this.post(`/customers/${customerId}/send-email`, { emails });
  }

  async sendCompanyReport(
    customerId: string,
    emails: string[],
    subject: string,
    message: string,
    excelFile: File,
    additionalFiles: File[]
  ): Promise<ApiResponse<{
    results: any[];
    successCount: number;
    failureCount: number;
  }>> {
    const formData = new FormData();
    formData.append('emails', JSON.stringify(emails));
    formData.append('subject', subject);
    formData.append('message', message);
    formData.append('report', excelFile);

    // Append all additional files
    additionalFiles.forEach((file) => {
      formData.append('additionalFiles', file);
    });

    const token = this.getAuthToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/customers/${customerId}/send-report`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Authentication required');
      }
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async getCustomerEmailReports(customerId: string): Promise<ApiResponse<{
    emailReports: any[];
    total: number;
  }>> {
    return this.get(`/customers/${customerId}/email-reports`);
  }

  async deleteCustomerEmailReport(customerId: string, reportId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/customers/${customerId}/email-reports/${reportId}`);
  }

  // User Management API methods (ashish@zellascreenings.com only)
  async getAllUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }): Promise<ApiResponse<{
    users: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalUsers: number;
      limit: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);

    const endpoint = queryParams.toString() ? `/users/all?${queryParams}` : '/users/all';
    return this.get(endpoint);
  }

  async getAdminUsers(): Promise<ApiResponse<{
    users: any[];
  }>> {
    return this.get('/users/admins');
  }

  async createAdminUser(userData: {
    name: string;
    designation?: string;
    phone?: string;
    email: string;
    password: string;
    role: 'admin' | 'super-admin';
  }): Promise<ApiResponse<{
    user: any;
  }>> {
    return this.post('/users/admins', userData);
  }

  async updateAdminUser(
    userId: string,
    updates: {
      name?: string;
      designation?: string;
      phone?: string;
      email?: string;
      password?: string;
      role?: 'admin' | 'super-admin';
      isActive?: boolean;
    }
  ): Promise<ApiResponse<{
    user: any;
  }>> {
    return this.put(`/users/admins/${userId}`, updates);
  }

  async deleteAdminUser(userId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/users/admins/${userId}`);
  }

  // Reports API methods
  async getReports(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    customerId?: string;
    sortBy?: string;
    sortOrder?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{
    reports: any[];
    currentPage: number;
    totalPages: number;
    totalReports: number;
  }> {
    const queryString = new URLSearchParams(
      Object.entries(params || {})
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    // Backend returns this structure directly, not wrapped in ApiResponse
    return this.get(`/reports${queryString ? `?${queryString}` : ''}`) as any;
  }

  async getReportById(reportId: string): Promise<any> {
    // Backend returns report directly, not wrapped in ApiResponse
    return this.get(`/reports/${reportId}`) as any;
  }

  async createReport(reportData: {
    reportType: string;
    description: string;
    customerId: string;
    emails: string[];
    dueDate?: string;
    priority?: string;
  }): Promise<ApiResponse<any>> {
    return this.post('/reports', reportData);
  }

  async uploadReportDocument(reportId: string, formData: FormData): Promise<any> {
    const token = this.getAuthToken();

    const response = await fetch(`${API_BASE_URL}/reports/${reportId}/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Upload failed');
    }

    // Backend returns report directly
    return data;
  }

  async deleteReportDocument(reportId: string, documentId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/reports/${reportId}/documents/${documentId}`);
  }

  async submitReport(reportId: string): Promise<any> {
    // Backend returns report directly
    return this.post(`/reports/${reportId}/submit`) as any;
  }

  async deleteReport(reportId: string): Promise<ApiResponse<{}>> {
    return this.delete(`/reports/${reportId}`);
  }

  async updateReportStatus(reportId: string, status: string, reviewNotes?: string): Promise<ApiResponse<any>> {
    return this.patch(`/reports/${reportId}/status`, { status, reviewNotes });
  }

  private async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Address Verification API methods
  async getAddressVerifications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    verificationStatus?: string;
    search?: string;
    vendor?: string;
    vendorWorkStatus?: string;
    assignedMember?: string;
    companyName?: string;
    city?: string;
    state?: string;
    dateFrom?: string;
    dateTo?: string;
    hasVendor?: 'true' | 'false';
  }): Promise<ApiResponse<{
    verifications: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.vendor) queryParams.append('vendor', params.vendor);
    if (params?.vendorWorkStatus) queryParams.append('vendorWorkStatus', params.vendorWorkStatus);
    if (params?.assignedMember) queryParams.append('assignedMember', params.assignedMember);
    if (params?.companyName) queryParams.append('companyName', params.companyName);
    if (params?.city) queryParams.append('city', params.city);
    if (params?.state) queryParams.append('state', params.state);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params?.hasVendor) queryParams.append('hasVendor', params.hasVendor);

    const queryString = queryParams.toString();
    return this.get(`/address-verifications${queryString ? `?${queryString}` : ''}`);
  }

  async bulkAssignVendor(ids: string[], vendor: string, price?: number | null): Promise<ApiResponse<{ updated: number; failed: number }>> {
    // price omitted → backend defaults to the vendor's rate (non-super-admins can't set it).
    return this.post('/address-verifications/bulk-assign', price != null ? { ids, vendor, price } : { ids, vendor });
  }

  // Vendor analytics (super-admin + admin)
  async getVendorAnalytics(params?: {
    vendorId?: string;
    dateFrom?: string;
    dateTo?: string;
    companyName?: string;
    city?: string;
    state?: string;
  }): Promise<ApiResponse<{ perVendor: any[]; totals: any }>> {
    const q = new URLSearchParams();
    if (params?.vendorId) q.append('vendorId', params.vendorId);
    if (params?.dateFrom) q.append('dateFrom', params.dateFrom);
    if (params?.dateTo) q.append('dateTo', params.dateTo);
    if (params?.companyName) q.append('companyName', params.companyName);
    if (params?.city) q.append('city', params.city);
    if (params?.state) q.append('state', params.state);
    const qs = q.toString();
    return this.get(`/vendors/analytics${qs ? `?${qs}` : ''}`);
  }

  async getVendorMemberAnalytics(vendorId: string): Promise<ApiResponse<{ members: any[] }>> {
    return this.get(`/vendors/${vendorId}/analytics/members`);
  }

  async getAddressVerificationStats(params?: {
    hasVendor?: 'true' | 'false';
  }): Promise<ApiResponse<{
    stats: {
      total: number;
      pending: number;
      verified: number;
      failed: number;
      linkSent: number;
      expired: number;
      successRate: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.hasVendor) queryParams.append('hasVendor', params.hasVendor);
    const queryString = queryParams.toString();
    return this.get(`/address-verifications/stats${queryString ? `?${queryString}` : ''}`);
  }

  async getAddressVerificationById(id: string): Promise<ApiResponse<any>> {
    return this.get(`/address-verifications/${id}`);
  }

  async createAddressVerification(data: any): Promise<ApiResponse<any>> {
    return this.post('/address-verifications', data);
  }

  /** Address suggestions for the case-creation search box (Places proxy). */
  async searchAddresses(query: string, session?: string): Promise<ApiResponse<Array<{
    placeId: string; description: string; mainText: string; secondaryText: string;
  }>>> {
    const params = new URLSearchParams({ q: query });
    if (session) params.set('session', session);
    return this.get(`/address-verifications/places/search?${params.toString()}`);
  }

  /** Resolve a picked suggestion to its formatted address and coordinates. */
  async getPlaceDetails(placeId: string, session?: string): Promise<ApiResponse<{
    formattedAddress: string; latitude?: number; longitude?: number;
    city?: string; state?: string; pin?: string;
  }>> {
    const params = new URLSearchParams({ placeId });
    if (session) params.set('session', session);
    return this.get(`/address-verifications/places/details?${params.toString()}`);
  }

  async updateAddressVerification(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/address-verifications/${id}`, data);
  }

  async deleteAddressVerification(id: string): Promise<ApiResponse<{}>> {
    return this.delete(`/address-verifications/${id}`);
  }

  async bulkCreateAddressVerifications(verifications: any[]): Promise<ApiResponse<{
    created: number;
    failed: number;
    errors?: any[];
  }>> {
    return this.post('/address-verifications/bulk', { verifications });
  }

  async sendVerificationLink(id: string): Promise<ApiResponse<{
    verificationToken: string;
    verificationLink: string;
    emailSent: boolean;
    expiresAt: string;
  }>> {
    return this.post(`/address-verifications/${id}/send-link`);
  }

  async adminUploadVerificationDocument(verificationId: string, file: File, docType: string): Promise<ApiResponse<any>> {
    const token = this.getAuthToken();
    const formData = new FormData();
    formData.append('document', file);
    formData.append('docType', docType);

    const response = await fetch(`${API_BASE_URL}/address-verifications/${verificationId}/upload-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Authentication required');
      }
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async adminDeleteVerificationDocument(verificationId: string, docType: string): Promise<ApiResponse<{}>> {
    return this.delete(`/address-verifications/${verificationId}/document/${docType}`);
  }

  // Generate report (HTML view in new tab)
  async viewAddressVerificationReport(id: string): Promise<void> {
    const token = this.getAuthToken();
    const url = `${API_BASE_URL}/address-verifications/${id}/report?format=html`;

    // Fetch the HTML report and open in new window
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate report' }));
        console.error('Report generation error:', errorData);
        throw new Error(errorData.message || 'Failed to generate report');
      }

      const html = await response.text();

      // Open in new window with HTML content
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(html);
        newWindow.document.close();
      }
    } catch (error) {
      console.error('View report error:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to generate report'}`);
      throw error;
    }
  }

  // Get report as HTML string
  async getAddressVerificationReportHTML(id: string): Promise<string> {
    const token = this.getAuthToken();
    const url = `${API_BASE_URL}/address-verifications/${id}/report?format=html`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load report' }));
        throw new Error(errorData.message || 'Failed to load report');
      }

      const html = await response.text();
      return html;
    } catch (error) {
      console.error('Get report HTML error:', error);
      throw error;
    }
  }

  // Download report as PDF
  async downloadAddressVerificationReport(id: string, code: string): Promise<void> {
    const token = this.getAuthToken();
    const url = `${API_BASE_URL}/address-verifications/${id}/report?format=pdf`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to download report' }));
        throw new Error(errorData.message || 'Failed to download report');
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/pdf')) {
        const text = await response.text();
        console.error('Expected PDF but got:', contentType, text.substring(0, 200));
        throw new Error('Invalid response format: Expected PDF but received ' + contentType);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Address_Verification_Report_${code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download report error:', error);
      alert(`Failed to download report: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  // Shared blob-download helper for the vendor report/photos PDF exports below.
  private async downloadBlob(url: string, options: RequestInit, filename: string, errorLabel: string): Promise<void> {
    const token = this.getAuthToken();
    try {
      const response = await fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `Failed to ${errorLabel}` }));
        throw new Error(errorData.message || `Failed to ${errorLabel}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(`${errorLabel} error:`, error);
      alert(`Failed to ${errorLabel}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  // Filesystem-safe slug for a filename part (candidate name, code, etc.).
  private slugForFilename(value: string, fallback = ''): string {
    return (value || fallback).trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || fallback;
  }

  // Vendor report/photos filename base: "<candidateName>_<code(BGVID)>".
  private vendorReportFileBase(name: string, code: string): string {
    return `${this.slugForFilename(name, 'candidate')}_${this.slugForFilename(code, 'case')}`;
  }

  // Vendor "Address Check" report PDF (table + verifier details, no photos) — single + bulk.
  async downloadVendorReport(id: string, code: string, name = ''): Promise<void> {
    await this.downloadBlob(
      `${API_BASE_URL}/address-verifications/${id}/vendor-report?format=pdf`,
      { method: 'GET' },
      `${this.vendorReportFileBase(name, code)}.pdf`,
      'download report'
    );
  }

  async downloadBulkVendorReports(ids: string[]): Promise<void> {
    await this.downloadBlob(
      `${API_BASE_URL}/address-verifications/vendor-report/bulk`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) },
      'vendor-reports.zip',
      'download reports'
    );
  }

  // Vendor "Field Photographs" PDF (photos only, no table) — a SEPARATE PDF
  // from the report above, not combined — single + bulk.
  async downloadVendorPhotosReport(id: string, code: string, name = ''): Promise<void> {
    await this.downloadBlob(
      `${API_BASE_URL}/address-verifications/${id}/vendor-report/photos?format=pdf`,
      { method: 'GET' },
      `${this.vendorReportFileBase(name, code)}_photos.pdf`,
      'download photos report'
    );
  }

  async downloadBulkVendorPhotosReports(ids: string[]): Promise<void> {
    await this.downloadBlob(
      `${API_BASE_URL}/address-verifications/vendor-report/photos/bulk`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) },
      'vendor-photos-reports.zip',
      'download photos reports'
    );
  }

  // Signed vendor agreement PDF (admin + super-admin). Only signed vendors have one.
  // Filename: "<vendorName>_<dateOfSign>_sign.pdf" (dateOfSign = YYYY-MM-DD).
  async downloadVendorAgreement(vendorId: string, vendorName: string, signedAt?: string | null): Promise<void> {
    const safeName = (vendorName || 'vendor').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
    const d = signedAt ? new Date(signedAt) : null;
    const signDate = d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : 'unknown';
    await this.downloadBlob(
      `${API_BASE_URL}/vendors/${vendorId}/agreement/pdf`,
      { method: 'GET' },
      `${safeName}_${signDate}_sign.pdf`,
      'download agreement'
    );
  }

  // Vendor-cases Excel export (admin + super-admin). Mirrors the vendor AV-tab
  // filters so the download matches the current filtered view. No pagination.
  async downloadVendorCasesExcel(filters?: {
    vendor?: string;
    vendorWorkStatus?: string;
    assignedMember?: string;
    status?: string;
    companyName?: string;
    city?: string;
    state?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }): Promise<void> {
    const queryParams = new URLSearchParams();
    if (filters?.vendor) queryParams.append('vendor', filters.vendor);
    if (filters?.vendorWorkStatus) queryParams.append('vendorWorkStatus', filters.vendorWorkStatus);
    if (filters?.assignedMember) queryParams.append('assignedMember', filters.assignedMember);
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.companyName) queryParams.append('companyName', filters.companyName);
    if (filters?.city) queryParams.append('city', filters.city);
    if (filters?.state) queryParams.append('state', filters.state);
    if (filters?.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) queryParams.append('dateTo', filters.dateTo);
    if (filters?.search) queryParams.append('search', filters.search);
    const qs = queryParams.toString();
    const stamp = new Date().toISOString().slice(0, 10);
    await this.downloadBlob(
      `${API_BASE_URL}/address-verifications/vendor-cases/export${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      `Vendor_Cases_${stamp}.xlsx`,
      'export vendor cases'
    );
  }

  // Public verification endpoints (no auth required)
  async getVerificationByToken(token: string): Promise<ApiResponse<any>> {
    return fetch(`${API_BASE_URL}/address-verifications/public/${token}`)
      .then(res => res.json());
  }

  async submitVerification(token: string, data: VerificationSubmitData): Promise<ApiResponse<any>> {
    return fetch(`${API_BASE_URL}/address-verifications/public/${token}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(res => res.json());
  }

  async uploadVerificationDocument(token: string, file: File, docType: string): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('docType', docType);

    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}/address-verifications/public/${token}/upload-document`, {
        method: 'POST',
        body: formData,
      });
    } catch {
      // fetch() only rejects on a network-layer failure. The browser's own
      // message is "Failed to fetch", which tells the user nothing.
      throw new Error(
        `Could not upload "${file.name}". Check your internet connection and try again — if you are on mobile data, moving to a stronger signal usually helps.`
      );
    }

    // A proxy or error page can return HTML; parsing it as JSON would throw a
    // SyntaxError that reads as a generic failure.
    const body = await res.json().catch(() => null);

    if (!res.ok) {
      const message = body?.message
        || (res.status === 413
          ? `"${file.name}" is too large to upload. Please use a smaller photo.`
          : `Upload of "${file.name}" failed (error ${res.status}). Please try again.`);
      throw new Error(message);
    }

    return body as ApiResponse<any>;
  }

  // ========== Document Collection API methods ==========

  async getDocumentCollections(params?: {
    page?: number;
    limit?: number;
    status?: string;
    verificationStatus?: string;
    search?: string;
    customerId?: string;
  }): Promise<ApiResponse<{
    collections: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.customerId) queryParams.append('customerId', params.customerId);

    const queryString = queryParams.toString();
    return this.get(`/document-collections${queryString ? `?${queryString}` : ''}`);
  }

  async getDocumentCollectionStats(params?: { customerId?: string }): Promise<ApiResponse<{
    stats: {
      total: number;
      pending: number;
      approved: number;
      rejected: number;
      linkSent: number;
      completed: number;
      successRate: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    const queryString = queryParams.toString();
    return this.get(`/document-collections/stats${queryString ? `?${queryString}` : ''}`);
  }

  async getDocumentCollectionCompanies(params?: { search?: string }): Promise<ApiResponse<{ companies: any[] }>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    return this.get(`/document-collections/companies${queryString ? `?${queryString}` : ''}`);
  }

  async sendAllDocumentCollectionLinks(customerId: string): Promise<ApiResponse<{ sent: number; failed: number; total: number }>> {
    return this.post('/document-collections/send-all-links', { customerId });
  }

  async getDocumentCollectionById(id: string): Promise<ApiResponse<any>> {
    return this.get(`/document-collections/${id}`);
  }

  async createDocumentCollection(data: any): Promise<ApiResponse<any>> {
    return this.post('/document-collections', data);
  }

  async updateDocumentCollection(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/document-collections/${id}`, data);
  }

  async deleteDocumentCollection(id: string): Promise<ApiResponse<{}>> {
    return this.delete(`/document-collections/${id}`);
  }

  async bulkCreateDocumentCollections(collections: any[]): Promise<ApiResponse<{
    created: number;
    failed: number;
    errors?: any[];
  }>> {
    return this.post('/document-collections/bulk', { collections });
  }

  async sendDocumentCollectionLink(id: string): Promise<ApiResponse<{
    verificationToken: string;
    verificationLink: string;
    emailSent: boolean;
    expiresAt: string;
  }>> {
    return this.post(`/document-collections/${id}/send-link`);
  }

  async adminUploadDocumentCollectionFile(collectionId: string, file: File, docType: string): Promise<ApiResponse<any>> {
    const token = this.getAuthToken();
    const formData = new FormData();
    formData.append('document', file);
    formData.append('docType', docType);

    const response = await fetch(`${API_BASE_URL}/document-collections/${collectionId}/upload-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Authentication required');
      }
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async generateDocumentCollectionDocx(id: string): Promise<ApiResponse<any>> {
    return this.post(`/document-collections/${id}/generate-docx`);
  }

  async downloadDocumentCollectionDocx(id: string): Promise<ApiResponse<{
    downloadUrl: string;
    fileName: string;
    generatedAt: string;
  }>> {
    return this.get(`/document-collections/${id}/download-docx`);
  }

  async deleteDocumentCollectionDocument(collectionId: string, docType: string): Promise<ApiResponse<any>> {
    return this.delete(`/document-collections/${collectionId}/documents/${docType}`);
  }

  async downloadAllDocumentCollectionDocuments(collectionId: string): Promise<void> {
    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/document-collections/${collectionId}/download-all-documents`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download documents');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'documents.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  // Public document collection endpoints (no auth)
  async getDocumentCollectionByToken(token: string): Promise<ApiResponse<any>> {
    return fetch(`${API_BASE_URL}/document-collections/public/${token}`)
      .then(res => res.json());
  }

  async saveDocumentCollectionProgress(token: string, data: { formData: any }): Promise<ApiResponse<any>> {
    return fetch(`${API_BASE_URL}/document-collections/public/${token}/save-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(res => res.json());
  }

  async submitDocumentCollection(token: string, data: any): Promise<ApiResponse<any>> {
    return fetch(`${API_BASE_URL}/document-collections/public/${token}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(res => res.json());
  }

  async uploadDocumentCollectionDocument(token: string, file: File, docType: string): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('docType', docType);

    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}/document-collections/public/${token}/upload-document`, {
        method: 'POST',
        body: formData,
      });
    } catch {
      // fetch() only rejects on a network-layer failure; the browser's own
      // message is "Failed to fetch", which tells the candidate nothing.
      throw new Error(
        `Could not upload "${file.name}". Check your internet connection and try again — if you are on mobile data, moving to a stronger signal usually helps.`
      );
    }

    const body = await res.json().catch(() => null);

    if (!res.ok) {
      const message = body?.message
        || (res.status === 413
          ? `"${file.name}" is too large to upload. Please use a smaller photo (limit 5MB).`
          : `Upload of "${file.name}" failed (error ${res.status}). Please try again.`);
      throw new Error(message);
    }

    return body as ApiResponse<any>;
  }

  // ========== Document Scanner (OCR) API methods ==========

  async scanDocuments(
    files: File[],
    docTypes: string[],
    candidateIndices?: number[]
  ): Promise<ApiResponse<{ jobId: string }>> {
    const token = this.getAuthToken();
    const formData = new FormData();
    files.forEach((f) => formData.append('documents', f));
    formData.append('docTypes', JSON.stringify(docTypes));
    if (candidateIndices && candidateIndices.length === files.length) {
      formData.append('candidateIndices', JSON.stringify(candidateIndices));
    }

    const response = await fetch(`${API_BASE_URL}/document-scanner/scan`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `Scan submit failed (${response.status})`);
    }
    return data;
  }

  async getScanJob(jobId: string): Promise<ApiResponse<{
    jobId: string;
    status: 'queued' | 'running' | 'done' | 'failed';
    progress: { total: number; done: number; failed: number; phase: string };
    docs: Array<{ docType: string; originalName: string; mime: string; pageCount: number; candidateIndex?: number; quality?: { score: number; warnings: string[] } | null; error: string | null }>;
    result?: {
      fields: Record<string, string | null> | null;
      provenance: Record<string, string | null> | null;
      warnings: string[];
      candidates?: Array<{
        index: number;
        fields: Record<string, string | null> | null;
        provenance: Record<string, string | null> | null;
        confidence?: Record<string, 'high' | 'low'>;
        error: string | null;
        tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
      }>;
    };
    tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
    error?: string | null;
    docUrls?: Array<{ docType: string; originalName: string; mime: string; url: string; candidateIndex?: number; quality?: { score: number; warnings: string[] } | null }>;
  }>> {
    return this.get(`/document-scanner/scan-jobs/${jobId}`);
  }

  async getScannerExcelHeaders(): Promise<ApiResponse<{ headers: string[]; fieldKeyToColumn: Record<string, number> }>> {
    return this.get('/document-scanner/excel-headers');
  }

  async listScannerExcels(): Promise<ApiResponse<{ excels: Array<{ id: string; name: string; rowCount: number; createdAt: string; updatedAt: string }> }>> {
    return this.get('/document-scanner/excels');
  }

  async createScannerExcel(name: string): Promise<ApiResponse<{ id: string; name: string; rowCount: number; createdAt: string }>> {
    return this.post('/document-scanner/excels', { name });
  }

  async appendScannerRow(
    excelId: string,
    body: { fields: Record<string, string | null>; provenance: Record<string, string | null>; sourceJobId?: string }
  ): Promise<ApiResponse<{ excel: { id: string; name: string; rowCount: number } }>> {
    return this.post(`/document-scanner/excels/${excelId}/rows`, body);
  }

  async appendScannerRowsBulk(
    excelId: string,
    body: {
      candidates: Array<{
        fields: Record<string, string | null>;
        provenance: Record<string, string | null>;
        sourceCandidateIndex?: number;
      }>;
      sourceJobId?: string;
    }
  ): Promise<ApiResponse<{
    appended: number;
    errors: Array<{ index: number | null; error: string }>;
    excel: { id: string; name: string; rowCount: number } | null;
  }>> {
    return this.post(`/document-scanner/excels/${excelId}/rows-bulk`, body);
  }

  async deleteScannerExcel(excelId: string): Promise<ApiResponse<{ id: string; rowsDeleted: number }>> {
    return this.delete(`/document-scanner/excels/${excelId}`);
  }

  async downloadScannerExcel(excelId: string, fallbackName?: string): Promise<void> {
    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/document-scanner/excels/${excelId}/download`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to download Excel');
    }
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = fallbackName ? `${fallbackName}.xlsx` : 'candidates.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const apiService = new ApiService();
export default apiService;